import { useCallback, type Dispatch, type SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';

import { AgendaViewModal } from 'components/admin/core/Settings/Dashboard/item/agendaViewItem/AgendaViewModal';
import type { AgendaViewItem as AgendaViewItemType } from 'types/dashboard';
import { useBooleanState } from 'utils/hooks';
import { Activities } from 'components/user/Overview/Activities';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface Props {
  agenda: AgendaViewItemType;
  removeItem: () => void;
  setAgendaViewData: Dispatch<SetStateAction<AgendaViewItemType>>;
}

export const AgendaViewItem = ({ agenda, removeItem, setAgendaViewData }: Props) => {
  const [modal, toggleModal] = useBooleanState(Boolean(agenda?.isNew) && agenda.type === 'AgendaView');
  const { t } = useTranslation();

  const typeKey = agenda?.type ? agenda.type.charAt(0).toLowerCase() + agenda.type.slice(1) : 'agendaView';

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: String(agenda.id),
    disabled: modal,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 10 : 'auto',
  };

  const handleCloseModal = useCallback(() => {
    toggleModal();
    if (agenda?.isNew) {
      removeItem();
    }
  }, [agenda?.isNew, removeItem, toggleModal]);

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <Activities title={agenda?.name || t(`core.settings.dashboard.${typeKey}`)} type={agenda?.type} onClick={toggleModal} onRemove={removeItem} />
      <AgendaViewModal open={modal} setAgendaViewData={setAgendaViewData} onClose={handleCloseModal} onConfirm={toggleModal} agendaData={agenda} />
    </div>
  );
};
